import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';
import * as XLSX from 'xlsx';
import {
    BarChart3,
    CalendarDays,
    Download,
    FileSpreadsheet,
    FileText,
    IndianRupee,
    Printer,
    ReceiptText,
    RefreshCcw,
    Search,
    ShieldCheck,
    Sparkles,
    TrendingUp,
    Users,
    WalletCards
} from 'lucide-react';
import Sidebar from '../../components/Sidebar/sidebar';
import Header from '../../components/Header';
import Pagination from '../../components/Pagination/Pagination';
import './premiumRegister.css';
import { API_BASE_URL } from '../../utils/apiBase';
import { toast } from '../../components/notifications/notificationService';

const EXCLUDED_EXCEL_COLUMNS = new Set([
    'Transaction Type',
    'Amount Received',
    'Amount Paid',
    'Mode of Payments',
    'Payment Type',
    'Cheque Number',
    'Bank Name',
    'Bank Branch',
    'Cheque Date',
    'DD Number'
]);

const PremiumRegister = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [financialYear, setFinancialYear] = useState('');
    const [clientType, setClientType] = useState('');
    const [insurer, setInsurer] = useState('');
    const [policyType, setPolicyType] = useState('');
    const [coverageType, setCoverageType] = useState('');
    const [assignedUser, setAssignedUser] = useState('');
    const [status, setStatus] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [reportData, setReportData] = useState([]);
    const [insurerOptions, setInsurerOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const showToast = useCallback((message) => {
        toast.success(message);
    }, []);

    const fetchData = useCallback(async (params = {}) => {
        setLoading(true);
        setError('');
        try {
            const res = await api.get(`${API_BASE_URL}/api/premium-register`, { params });
            setReportData(res.data || []);
            showToast('Premium register updated');
        } catch (err) {
            console.error(err);
            setError('Failed to load premium register data. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    const fetchInsurers = useCallback(async () => {
        try {
            const res = await api.get(`${API_BASE_URL}/api/premium-register/insurers`);
            setInsurerOptions(res.data || []);
        } catch (err) {
            console.error(err);
        }
    }, []);

    useEffect(() => {
        fetchData();
        fetchInsurers();
    }, [fetchData, fetchInsurers]);

    const parseNumber = (value) => {
        const number = Number(String(value || '').replace(/,/g, ''));
        return Number.isFinite(number) ? number : 0;
    };

    const formatCurrency = (value) =>
        new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(value || 0);

    const normalizePolicyNumber = (value) => {
        const policyNumber = (value || '').toString().trim();
        return policyNumber.endsWith(' END') ? policyNumber.slice(0, -4).trim() : policyNumber;
    };

    const uniqueValues = useCallback((key) => {
        const values = reportData.map(row => row[key]).filter(Boolean);
        return [...new Set(values)].sort();
    }, [reportData]);

    const financialYearOptions = useMemo(() => {
        const years = new Set();
        reportData.forEach(row => {
            if (!row['Premium Transaction Date']) return;
            const date = new Date(row['Premium Transaction Date']);
            if (Number.isNaN(date.getTime())) return;
            const startYear = date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
            years.add(`${startYear}-${String(startYear + 1).slice(-2)}`);
        });
        return [...years].sort().reverse();
    }, [reportData]);

    const policyOptions = useMemo(() => uniqueValues('Policy'), [uniqueValues]);
    const clientTypeOptions = useMemo(() => uniqueValues('Client Type'), [uniqueValues]);
    const paymentOptions = useMemo(() => uniqueValues('Mode of Payments'), [uniqueValues]);
    const statusOptions = useMemo(() => uniqueValues('Payment Type'), [uniqueValues]);

    const matchesFinancialYear = useCallback((dateValue) => {
        if (!financialYear || !dateValue) return true;
        const date = new Date(dateValue);
        if (Number.isNaN(date.getTime())) return true;
        const [startYear] = financialYear.split('-').map(Number);
        const fyStart = new Date(startYear, 3, 1);
        const fyEnd = new Date(startYear + 1, 2, 31, 23, 59, 59, 999);
        return date >= fyStart && date <= fyEnd;
    }, [financialYear]);

    const filteredData = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();

        return reportData.filter(row => {
            const searchable = [
                row.Client,
                row['Policy Number'],
                row['Invoice Number'],
                row['Insurance Company']
            ].join(' ').toLowerCase();

            return (
                (!query || searchable.includes(query)) &&
                matchesFinancialYear(row['Premium Transaction Date']) &&
                (!clientType || row['Client Type'] === clientType) &&
                (!insurer || row['Insurance Company'] === insurer) &&
                (!policyType || row.Policy === policyType) &&
                (!coverageType || row['Mode of Payments'] === coverageType) &&
                (!assignedUser || (row['Assigned User'] || '').toLowerCase().includes(assignedUser.toLowerCase())) &&
                (!status || row['Payment Type'] === status)
            );
        });
    }, [reportData, searchTerm, matchesFinancialYear, clientType, insurer, policyType, coverageType, assignedUser, status]);

    const pageLast = currentPage * itemsPerPage;
    const pageFirst = pageLast - itemsPerPage;
    const currentData = filteredData.slice(pageFirst, pageLast);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, startDate, endDate, financialYear, clientType, insurer, policyType, coverageType, assignedUser, status, itemsPerPage]);

    useEffect(() => {
        const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
        setCurrentPage((page) => Math.min(page, totalPages));
    }, [filteredData.length, itemsPerPage]);

    const summary = useMemo(() => {
        const grossPremium = filteredData.reduce((sum, row) => sum + parseNumber(row['Gross Premium']), 0);
        const netPremium = filteredData.reduce((sum, row) => sum + parseNumber(row['Net Premium']), 0);
        const brokerage = filteredData.reduce((sum, row) => sum + parseNumber(row['Total Brokerage']), 0);
        const received = filteredData.reduce((sum, row) => sum + parseNumber(row['Amount Received']), 0);

        return {
            grossPremium,
            policies: filteredData.length,
            pending: Math.max(grossPremium - received, 0),
            received,
            netPremium,
            brokerage
        };
    }, [filteredData]);

    const handleApplyFilter = () => {
        if ((startDate && !endDate) || (!startDate && endDate)) {
            setError('Please select both start date and end date.');
            return;
        }

        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            setError('Start date cannot be after end date.');
            return;
        }

        const params = {};
        if (startDate && endDate) {
            params.start = startDate;
            params.end = endDate;
        }
        if (insurer) {
            params.insurer = insurer;
        }

        fetchData(params);
    };

    const handleClearFilter = () => {
        setStartDate('');
        setEndDate('');
        setFinancialYear('');
        setClientType('');
        setInsurer('');
        setPolicyType('');
        setCoverageType('');
        setAssignedUser('');
        setStatus('');
        setSearchTerm('');
        fetchData();
    };

    const prepareExportRows = () => filteredData.map(row => Object.fromEntries(
        Object.entries({
            ...row,
            'Policy Number': normalizePolicyNumber(row['Policy Number'])
        }).filter(([column]) => !EXCLUDED_EXCEL_COLUMNS.has(column))
    ));

    const exportToExcel = () => {
        if (!filteredData.length) {
            setError('No report rows are available to export.');
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(prepareExportRows());
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Premium Register');
        XLSX.writeFile(workbook, `Premium_Register_${startDate || 'ALL'}_to_${endDate || 'ALL'}.xlsx`);
        showToast('Excel export downloaded');
    };

    const exportToPDF = () => {
        if (!filteredData.length) {
            setError('No report rows are available to export.');
            return;
        }
        window.print();
        showToast('Print view opened for PDF export');
    };

    const renderPolicyNumberCell = (value) => {
        const text = (value || '').toString().trim();
        if (!text) return '-';

        if (text.endsWith(' END')) {
            return (
                <span className="pr-policy-number-cell">
                    <span>{text.slice(0, -4).trim()}</span>
                    <span className="pr-end-tag">END</span>
                </span>
            );
        }

        return text;
    };

    const visibleColumns = [
        'Type',
        'Policy Number',
        'Client',
        'Premium Transaction Date',
        'Start Date',
        'Policy',
        'Insurance Company',
        'Client Type',
        'Gross Premium',
        'Net Premium',
        'Total Brokerage',
        'Payment Type',
        'Mode of Payments'
    ];

    const summaryCards = [
        { label: 'Total Premium', value: formatCurrency(summary.grossPremium), note: 'Gross premium value', icon: IndianRupee, tone: 'is-blue' },
        { label: 'Total Policies', value: summary.policies, note: 'Filtered records', icon: ReceiptText, tone: 'is-green' },
        { label: 'Pending Premium', value: formatCurrency(summary.pending), note: 'Awaiting receipt', icon: WalletCards, tone: 'is-amber' },
        { label: 'Received Premium', value: formatCurrency(summary.received), note: 'Collected amount', icon: ShieldCheck, tone: 'is-teal' },
        { label: 'Net Premium', value: formatCurrency(summary.netPremium), note: 'After adjustments', icon: BarChart3, tone: 'is-indigo' },
        { label: 'Brokerage Amount', value: formatCurrency(summary.brokerage), note: 'Receivable brokerage', icon: Users, tone: 'is-rose' }
    ];

    return (
        <div className="premium-register-container premium-register-shell">
            <div className="sidebar">
                <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
            </div>

            <div className={`premium-register-content ${collapsed ? 'collapsed' : ''}`}>
                <div className="premium-register-sticky-header">
                    <Header />
                </div>

                <main className="premium-register-scroll-area pr-page">
                    <section className="pr-hero">
                        <div>
                            <div className="pr-eyebrow"><Sparkles size={15} /> Reports Dashboard</div>
                            <h1>Premium Register</h1>
                            <p>View and manage premium register reports efficiently with fast filtering, export-ready data, and sticky financial columns.</p>
                        </div>
                        <div className="pr-hero-stat">
                            <span>Total Records</span>
                            <strong>{filteredData.length}</strong>
                            <small>{reportData.length} loaded</small>
                        </div>
                    </section>

                    <section className="pr-summary-grid">
                        {summaryCards.map(({ label, value, note, icon: Icon, tone }) => (
                            <div className={`pr-summary-card ${tone}`} key={label}>
                                <Icon size={22} />
                                <span>{label}</span>
                                <strong>{value}</strong>
                                <small><TrendingUp size={13} /> {note}</small>
                            </div>
                        ))}
                    </section>

                    <section className="pr-filter-card">
                        <div className="pr-filter-header">
                            <div>
                                <h2>Report Filters</h2>
                                <p>Refine the register by period, insurer, policy, payment mode, and status.</p>
                            </div>
                            <div className="pr-search-wrap">
                                <Search size={18} />
                                <input
                                    type="search"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search client, policy, invoice, insurer"
                                />
                            </div>
                        </div>

                        {error && <div className="pr-alert">{error}</div>}

                        <div className="pr-filter-grid">
                            <label>
                                <span>Premium Transaction Date Range</span>
                                <div className="pr-date-range">
                                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                                </div>
                            </label>

                            <label>
                                <span>Financial Year</span>
                                <select value={financialYear} onChange={(e) => setFinancialYear(e.target.value)}>
                                    <option value="">All Years</option>
                                    {financialYearOptions.map(option => <option key={option} value={option}>{option}</option>)}
                                </select>
                            </label>

                            <label>
                                <span>Client Type</span>
                                <select value={clientType} onChange={(e) => setClientType(e.target.value)}>
                                    <option value="">All Clients</option>
                                    {clientTypeOptions.map(option => <option key={option} value={option}>{option}</option>)}
                                </select>
                            </label>

                            <label>
                                <span>Insurer</span>
                                <select value={insurer} onChange={(e) => setInsurer(e.target.value)}>
                                    <option value="">All Insurers</option>
                                    {insurerOptions.map(option => <option key={option} value={option}>{option}</option>)}
                                </select>
                            </label>

                            <label>
                                <span>Policy Type</span>
                                <select value={policyType} onChange={(e) => setPolicyType(e.target.value)}>
                                    <option value="">All Policies</option>
                                    {policyOptions.map(option => <option key={option} value={option}>{option}</option>)}
                                </select>
                            </label>

                            <label>
                                <span>Coverage Type</span>
                                <select value={coverageType} onChange={(e) => setCoverageType(e.target.value)}>
                                    <option value="">All Modes</option>
                                    {paymentOptions.map(option => <option key={option} value={option}>{option}</option>)}
                                </select>
                            </label>

                            <label>
                                <span>Assigned User</span>
                                <input value={assignedUser} onChange={(e) => setAssignedUser(e.target.value)} placeholder="User name" />
                            </label>

                            <label>
                                <span>Status</span>
                                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                                    <option value="">All Status</option>
                                    {statusOptions.map(option => <option key={option} value={option}>{option}</option>)}
                                </select>
                            </label>
                        </div>

                        <div className="pr-action-row">
                            <button className="pr-btn pr-btn-primary" onClick={handleApplyFilter} disabled={loading}>
                                {loading ? <RefreshCcw className="pr-spin" size={17} /> : <CalendarDays size={17} />}
                                {loading ? 'Generating...' : 'Generate Report'}
                            </button>
                            <button className="pr-btn pr-btn-success" onClick={exportToExcel} disabled={!filteredData.length}>
                                <FileSpreadsheet size={17} /> Export Excel
                            </button>
                            <button className="pr-btn pr-btn-danger" onClick={exportToPDF} disabled={!filteredData.length}>
                                <FileText size={17} /> Export PDF
                            </button>
                            <button className="pr-btn pr-btn-ghost" onClick={() => window.print()} disabled={!filteredData.length}>
                                <Printer size={17} /> Print View
                            </button>
                            <button className="pr-btn pr-btn-light" onClick={handleClearFilter} disabled={loading}>
                                <RefreshCcw size={17} /> Reset Filters
                            </button>
                        </div>
                    </section>

                    <section className="pr-table-card">
                        <div className="pr-table-toolbar">
                            <div>
                                <h2>Premium Report Details</h2>
                                <p>{filteredData.length} rows matched from {reportData.length} loaded records</p>
                            </div>
                            <span><Download size={15} /> Export-ready register</span>
                        </div>

                        <div className="pr-table-scroll">
                            <table className="pr-report-table">
                                <thead>
                                    <tr>
                                        {visibleColumns.map((key) => (
                                            <th
                                                key={key}
                                                className={key === 'Policy Number' ? 'pr-sticky-col pr-sticky-policy' : ''}
                                            >
                                                {key === 'Client' ? 'Client Name' : key === 'Policy' ? 'Policy Type' : key}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        Array.from({ length: 8 }).map((_, rowIndex) => (
                                            <tr key={`skeleton-${rowIndex}`} className="pr-skeleton-row">
                                                {visibleColumns.map((col) => (
                                                    <td
                                                        key={col}
                                                        data-label={col === 'Client' ? 'Client Name' : col === 'Policy' ? 'Policy Type' : col}
                                                        className={col === 'Policy Number' ? 'pr-sticky-col pr-sticky-policy' : ''}
                                                    >
                                                        <span />
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    ) : filteredData.length === 0 ? (
                                        <tr>
                                            <td className="pr-empty-state" colSpan={visibleColumns.length}>
                                                <ReceiptText size={34} />
                                                <strong>No premium records found</strong>
                                                <span>Adjust filters or generate the report for a wider date range.</span>
                                            </td>
                                        </tr>
                                    ) : (
                                        currentData.map((row, i) => (
                                            <tr key={`${row['Policy Number'] || 'row'}-${pageFirst + i}`}>
                                                {visibleColumns.map((col) => (
                                                    <td
                                                        key={col}
                                                        data-label={col === 'Client' ? 'Client Name' : col === 'Policy' ? 'Policy Type' : col}
                                                        className={`${col === 'Client' ? 'pr-client-cell' : ''} ${col === 'Policy Number' ? 'pr-sticky-col pr-sticky-policy' : ''} ${['Gross Premium', 'Net Premium', 'Total Brokerage'].includes(col) ? 'pr-amount-cell' : ''}`}
                                                    >
                                                        {col === 'Policy Number'
                                                            ? renderPolicyNumberCell(row[col])
                                                            : col === 'Payment Type'
                                                                ? <span className="pr-status-badge">{row[col] || 'Pending'}</span>
                                                                : ['Gross Premium', 'Net Premium', 'Total Brokerage'].includes(col)
                                                                    ? formatCurrency(parseNumber(row[col]))
                                                                    : (row[col] || '-')}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {filteredData.length > 0 && (
                            <div className="pr-pagination-row">
                                <div className="pr-pagination-meta">
                                    <span>
                                        Showing {pageFirst + 1}-{Math.min(pageLast, filteredData.length)} of {filteredData.length}
                                    </span>
                                    <small>Filtered premium register records</small>
                                </div>

                                <div className="pr-pagination-controls">
                                    <Pagination
                                        totalItems={filteredData.length}
                                        itemsPerPage={itemsPerPage}
                                        currentPage={currentPage}
                                        onPageChange={setCurrentPage}
                                    />
                                </div>

                                <div className="pr-items-per-page">
                                    <label>Items per page:</label>
                                    <select
                                        value={itemsPerPage}
                                        onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                    >
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </section>
                </main>
            </div>
        </div>
    );
};

export default PremiumRegister;
